import { BaseCustomizationEntry } from "../../Redux/reduxState";
import { VoiceCategory, VoiceSubCategory, UISelectorType } from "../../utilities/types";

export interface VoiceCustomizationEntry extends BaseCustomizationEntry {
  category: VoiceCategory,
  subCategory?: VoiceSubCategory,
}

const generalEntries: VoiceCustomizationEntry[] = [
  {
    id: 'agentName',
    displayName: 'Agent Name',
    category: VoiceCategory.General,
    subCategory: VoiceSubCategory.Common,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'startDirectlyOnVoice',
    displayName: 'Start in Voice Mode',
    category: VoiceCategory.General,
    subCategory: VoiceSubCategory.Common,
    uiSelectorType: UISelectorType.booleanSelector,
    containerName: "root"
  },
  {
    id: 'voiceSwitchToChatButtonEnabled',
    displayName: 'Show Switch to Webchat Button',
    category: VoiceCategory.General,
    subCategory: VoiceSubCategory.Behavior,
    uiSelectorType: UISelectorType.booleanSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'voiceSnippetToChatOnClose',
    displayName: 'Return to Webchat on Call End/Close',
    category: VoiceCategory.General,
    subCategory: VoiceSubCategory.Behavior,
    uiSelectorType: UISelectorType.booleanSelector,
    containerName: "VoiceUI",
    disabled: ['voiceSwitchToChatButtonEnabled']
  },
  {
    id: 'voiceTranscriptEnabled',
    displayName: 'Enable Transcript Button',
    category: VoiceCategory.General,
    subCategory: VoiceSubCategory.Behavior,
    uiSelectorType: UISelectorType.booleanSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'voiceTimerEnabled',
    displayName: 'Enable Call Timer',
    category: VoiceCategory.General,
    subCategory: VoiceSubCategory.Behavior,
    uiSelectorType: UISelectorType.booleanSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'voiceMicVolume',
    displayName: 'Microphone Volume (0-1)',
    category: VoiceCategory.General,
    subCategory: VoiceSubCategory.Behavior,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'voiceBotVolume',
    displayName: 'Bot Volume (0-1)',
    category: VoiceCategory.General,
    subCategory: VoiceSubCategory.Behavior,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  },
];

const fontEntries: VoiceCustomizationEntry[] = [
  {
    id: 'fontSizeSmall',
    displayName: 'Font Size Small',
    category: VoiceCategory.Fonts,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'fontSizeMedium',
    displayName: 'Font Size Medium',
    category: VoiceCategory.Fonts,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'fontSizeLarge',
    displayName: 'Font Size Large',
    category: VoiceCategory.Fonts,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
];

const controlEntries: VoiceCustomizationEntry[] = [
  {
    id: 'width',
    displayName: 'Voice Shell Width',
    category: VoiceCategory.Control,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'height',
    displayName: 'Voice Shell Height',
    category: VoiceCategory.Control,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'minWidth',
    displayName: 'Voice Shell Minimum Width',
    category: VoiceCategory.Control,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'maxWidth',
    displayName: 'Voice Shell Maximum Width',
    category: VoiceCategory.Control,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'minHeight',
    displayName: 'Voice Shell Minimum Height',
    category: VoiceCategory.Control,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'maxHeight',
    displayName: 'Voice Shell Maximum Height',
    category: VoiceCategory.Control,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.defaultSelector,
    containerName: "VoiceUI"
  }
];

const outboundEntries: VoiceCustomizationEntry[] = [
  {
    id: 'showOutboundCallButton',
    displayName: 'Show Outbound Call Button',
    category: VoiceCategory.Outbound,
    subCategory: VoiceSubCategory.Common,
    uiSelectorType: UISelectorType.booleanSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'outboundCallDelayMs',
    displayName: 'Outbound Call Delay (ms)',
    category: VoiceCategory.Outbound,
    subCategory: VoiceSubCategory.Common,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'outboundCampaigns',
    displayName: 'Outbound Campaigns (one per line: Name|Id)',
    category: VoiceCategory.Outbound,
    subCategory: VoiceSubCategory.Common,
    uiSelectorType: UISelectorType.defaultSelector,
    multiline: true,
    containerName: "VoiceUI"
  },
  {
    id: 'outboundPrepareAnimation',
    displayName: 'Preparing Call Animation',
    category: VoiceCategory.Outbound,
    subCategory: VoiceSubCategory.Animation,
    uiSelectorType: UISelectorType.dropDownSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'outboundCallingAnimation',
    displayName: 'Calling Animation',
    category: VoiceCategory.Outbound,
    subCategory: VoiceSubCategory.Animation,
    uiSelectorType: UISelectorType.dropDownSelector,
    containerName: "VoiceUI"
  }
];

const themeEntries: VoiceCustomizationEntry[] = [
  {
    id: 'theme',
    displayName: 'Theme',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Common,
    uiSelectorType: UISelectorType.dropDownSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'tone',
    displayName: 'Tone (surface darkness, 0-100)',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Common,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'primary',
    displayName: 'Primary Color',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'secondary',
    displayName: 'Secondary Color',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'accent',
    displayName: 'Accent Color',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'animation',
    displayName: 'Animation',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Gradient,
    uiSelectorType: UISelectorType.dropDownSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'angle',
    displayName: 'Gradient Angle (degrees)',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Gradient,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'stop',
    displayName: 'Secondary Color Stop (0-100)',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Gradient,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'intensity',
    displayName: 'Intensity (0-100)',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Gradient,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'background',
    displayName: 'Ambient Background (0-100)',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Gradient,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'spread',
    displayName: 'Ambient Spread (0-100)',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Gradient,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'blur',
    displayName: 'Ambient Blur (px)',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Gradient,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'glow',
    displayName: 'Ambient Glow (0-100)',
    category: VoiceCategory.Theme,
    subCategory: VoiceSubCategory.Gradient,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
];

const orbEntries: VoiceCustomizationEntry[] = [
  {
    id: 'voiceOrbSize',
    displayName: 'Orb Size (px)',
    category: VoiceCategory.Orb,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
];

const controlsEntries: VoiceCustomizationEntry[] = [
  {
    id: 'controlButtonSize',
    displayName: 'Button Size (px)',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlButtonsGap',
    displayName: 'Buttons Gap (px)',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlPaddingTopBottom',
    displayName: 'Padding Top and Bottom (px)',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlButtonBackdropBlur',
    displayName: 'Backdrop Blur (px)',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Layout,
    uiSelectorType: UISelectorType.integerSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlButtonBackgroundColor',
    displayName: 'Background Color',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlButtonBackgroundColorOnHover',
    displayName: 'Background Color On Hover',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlButtonIconColor',
    displayName: 'Icon Color',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlButtonBorderColor',
    displayName: 'Border Color',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlButtonActiveBackgroundColor',
    displayName: 'Active Background Color',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlButtonActiveIconColor',
    displayName: 'Active Icon Color',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'controlHangupButtonColor',
    displayName: 'Hangup Pill Text/Icon Color',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'hangupButtonBackgroundColor',
    displayName: 'Hangup Button Background Color',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'hangupButtonIconColor',
    displayName: 'Hangup Button Icon Color',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Colors,
    uiSelectorType: UISelectorType.colorSelector,
    containerName: "VoiceUI"
  },
  {
    id: 'v2_hangup_icon',
    displayName: 'Hangup icon',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Icons,
    uiSelectorType: UISelectorType.defaultSelector,
    multiline: true,
    containerName: "VoiceUI"
  },
  {
    id: 'v2_microphone_icon',
    displayName: 'Microphone icon',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Icons,
    uiSelectorType: UISelectorType.defaultSelector,
    multiline: true,
    containerName: "VoiceUI"
  },
  {
    id: 'v2_microphone_muted_icon',
    displayName: 'Microphone muted icon',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Icons,
    uiSelectorType: UISelectorType.defaultSelector,
    multiline: true,
    containerName: "VoiceUI"
  },
  {
    id: 'v2_transcript_icon',
    displayName: 'Show transcript icon',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Icons,
    uiSelectorType: UISelectorType.defaultSelector,
    multiline: true,
    containerName: "VoiceUI"
  },
  {
    id: 'v2_orb_icon',
    displayName: 'Show orb icon',
    category: VoiceCategory.Controls,
    subCategory: VoiceSubCategory.Icons,
    uiSelectorType: UISelectorType.defaultSelector,
    multiline: true,
    containerName: "VoiceUI"
  },
];

const localizationEntry: VoiceCustomizationEntry = {
  id: 'localization',
  displayName: 'Localization',
  category: VoiceCategory.Localization,
  uiSelectorType: UISelectorType.localizationSelector,
  containerName: "VoiceUI"
};

const searchEntry: VoiceCustomizationEntry = {
  id: 'search',
  displayName: 'Search',
  category: VoiceCategory.Search,
  uiSelectorType: UISelectorType.searchSelector,
  containerName: "root"
};

export const voiceCustomizationEntries: VoiceCustomizationEntry[] = [
  searchEntry,
  ...generalEntries,
  ...fontEntries,
  ...controlEntries,
  ...themeEntries,
  ...orbEntries,
  ...controlsEntries,
  ...outboundEntries,
  localizationEntry,
];
